import { useRef, useState } from 'react'

/**
 * One asynchronous action, tracked per ROW rather than by the underlying
 * mutation's own `isPending`.
 *
 * Extracted from three near-identical copies: `InvitationRow`'s `resending`,
 * `RoleMenu`'s `changing`/`failure`, and now `MemberActionsMenu`'s remove —
 * `RoleMenu`'s own docblock named this exact moment ("Task 6's remove will be
 * the third. Extract a `useRowMutation()` then, not now.").
 *
 * **Deliberately per-ROW, not the mutation hook's own `isPending`.**
 * `useChangeRole`, `useResendInvitation` and `useRemoveMember` are each
 * called ONCE, in `Household.tsx`, for the whole list — their `isPending` is
 * one flag shared by every row. Using it here would disable every row's
 * control for the ONE row whose request is in flight, and report
 * "sending"/"changing" on rows that are not. Each row therefore keeps this
 * hook's own state instead — one instance per mounted control.
 *
 * `run` refuses to start a second call while the first is still in flight —
 * it returns the SAME in-flight promise rather than sending a duplicate
 * request. It clears any previous failure before starting, and on rejection
 * records `describeFailure(cause)`'s message — when a caller wants one; a
 * control whose failure is already rendered by something else
 * (`MemberActionsMenu`'s remove goes through `ConfirmDialog`, which shows its
 * own reason on rejection) can omit it — before RE-THROWING, so a caller that
 * needs the rejection itself still gets it.
 *
 * **`describeFailure` receives the rejection (issue 359).** It used to take
 * nothing, which forced every caller to one fixed sentence and is why both
 * household rows discarded the server's specific rejection and told the user
 * to retry three things that retrying cannot fix. The cause is passed so a
 * caller can read `HouseholdError`'s `status` and `detail` — see
 * `rowFailure.ts` for the mapping and for why the strings are a contract. `ConfirmDialog`'s own `onConfirm` contract
 * is exactly that: it awaits, and on rejection stays open and shows the
 * reason, which only works if the promise it awaits still rejects.
 *
 * **This guard covers a window the call sites cannot, and that window is
 * narrower than it first looks — say what is true, not what would be tidy.**
 * Read the #362 paragraph at the end before trusting the survey that follows.
 * A call site's `pending` is state, still stale in the handler's closure until
 * React re-renders; this ref is assigned synchronously. So between a first
 * click and that re-render, only this line is armed.
 *
 * **Measured, not reasoned:** deleting this line leaves every unit test green
 * and four of `invitation-resend-focus.spec.ts`'s five e2e tests green —
 * including two real Playwright clicks with nothing awaited between them. Only
 * the fifth reds, where both clicks are dispatched inside one synchronous
 * `evaluate()` callback in the same JS task. **A real user cannot reach this
 * window**; two genuine clicks are separate tasks roughly 100ms apart with a
 * re-render in between. So this is defence against a same-task double dispatch
 * that only code can produce — worth keeping, and not the thing standing
 * between a person and a double-fire.
 *
 * If you delete it, one e2e test reds. Nothing else will.
 * `t6-tester.md` mutation-tested it directly (deleted the `if
 * (inFlight.current) return inFlight.current` line) and found every one of
 * the three call sites already refuses re-entry on its OWN, before `run` is
 * ever reached a second time: at the time, `InvitationRow`'s resend button
 * carried a native `disabled`, which `userEvent`/a real click both honour;
 * `RoleMenu`'s trigger and `choose()` both check `if (changeMutation.pending)
 * return` ahead of any call (Task 5's own investigation into this exact
 * question, on the predecessor of this hook, found nothing that could reach
 * the second copy either); `MemberActionsMenu`'s trigger guards on `pending`
 * the same way, and `ConfirmDialog` ALSO refuses a second confirm with its
 * own `if (busy) return` plus a disabled confirm button. So deleting this
 * line broke only `useRowMutation.test.tsx`'s own direct test of it —
 * nothing else in the suite moved. Kept anyway, cheap and correct: it is
 * what stops a call site that later drops its OWN guard (there are now
 * three hand-written copies of "don't re-enter" to keep in sync, one per
 * call site) from silently becoming exactly that double-fire.
 *
 * **#362 did exactly that to `InvitationRow`, on purpose, and this is the
 * updated picture.** Its native `disabled` is gone — disabling the FOCUSED
 * button drops keyboard focus to `<body>` in Chromium with nothing to
 * restore it — so its `if (resendMutation.pending) return` is no longer
 * backed by the platform, only by this hook. Mutation-confirmed:
 * `InvitationRow.actions.test.tsx`'s double-submit test stays green deleting
 * either guard alone (the other one catches it) and reds only when both are
 * deleted together. So for THIS call site specifically, this guard is not a
 * backstop with nothing behind it if it goes — it is one of two independent
 * guards, same as `RoleMenu`'s and `MemberActionsMenu`'s always were, and a
 * reader should not assume `disabled` is doing any work here anymore.
 */
export function useRowMutation<Args extends unknown[], T>(
  action: (...args: Args) => Promise<T>,
  describeFailure?: (cause: unknown) => string,
): { run: (...args: Args) => Promise<T>; pending: boolean; failure: string | null } {
  const [pending, setPending] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const inFlight = useRef<Promise<T> | null>(null)

  function run(...args: Args): Promise<T> {
    if (inFlight.current) return inFlight.current
    setFailure(null)
    setPending(true)
    const promise = action(...args)
      .catch((thrown: unknown) => {
        if (describeFailure) setFailure(describeFailure(thrown))
        throw thrown
      })
      .finally(() => {
        setPending(false)
        inFlight.current = null
      })
    inFlight.current = promise
    return promise
  }

  return { run, pending, failure }
}
